import {
  Badge,
  Button,
  ChannelMark,
  buttonVariants,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
  EmptyState,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  Spinner,
  Tooltip,
  useToast,
} from "@forge/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Cable,
  Link2,
  Radio,
  MessageCircleQuestion,
  MessageSquare,
  Eye,
  Megaphone,
  Scale,
  Pencil,
  Plus,
  Receipt,
  ShoppingBag,
  Store,
  Tags,
  Unlink,
  Layers,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { PageHeader } from "../../components/page-header";
import { trackEventOnce } from "../../lib/analytics";
import { api, unwrap } from "../../lib/api";
import { channelLabel } from "../../lib/channel";
import { formatDateTime, formatRelativeTime } from "../../lib/format";

type ConnectionsSearch = { status?: string };

export const Route = createFileRoute("/_app/connections")({
  validateSearch: (search: Record<string, unknown>): ConnectionsSearch => ({
    status: typeof search.status === "string" ? search.status : undefined,
  }),
  component: ConnectionsPage,
});

const ACCOUNT_STATUS: Record<
  string,
  { label: string; variant: "success" | "warning" | "danger" }
> = {
  connected: { label: "Conectada", variant: "success" },
  token_expired: { label: "Token expirado", variant: "warning" },
  revoked: { label: "Acesso revogado", variant: "danger" },
  disconnected: { label: "Desvinculada", variant: "danger" },
};

function ConnectionsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  // Feedback after returning from the ML OAuth flow. The toast manager is a
  // new object every render — putting it in the deps looped this effect and
  // blanked the page (maximum update depth).
  useEffect(() => {
    if (!search.status) return;
    if (search.status === "connected") {
      trackEventOnce("StoreConnected", "fc-analytics-store-connected");
      toastRef.current.add({
        type: "success",
        title: "Conta conectada com sucesso",
      });
    } else if (search.status === "account_limit") {
      toastRef.current.add({
        type: "error",
        title: "Limite de lojas do seu plano atingido",
      });
    } else if (search.status === "account_taken") {
      toastRef.current.add({
        type: "error",
        title: "Esta loja já está conectada em outra organização",
      });
    } else {
      toastRef.current.add({
        type: "error",
        title: "Não foi possível conectar a conta",
        description: "Tente novamente ou verifique as credenciais do app.",
      });
    }
    void navigate({ search: {}, replace: true });
  }, [search.status, navigate]);

  const accounts = useQuery({
    queryKey: ["ml-accounts"],
    queryFn: async () => unwrap(await api.ml.accounts.get()),
  });

  const imports = useQuery({
    queryKey: ["imports"],
    queryFn: async () => unwrap(await api.imports.get()),
    refetchInterval: (query) =>
      (query.state.data?.jobs.length ?? 0) > 0 ? 3000 : false,
  });

  const billing = useQuery({
    queryKey: ["billing"],
    queryFn: async () => unwrap(await api.billing.get()),
  });

  const shopee = useQuery({
    queryKey: ["shopee-status"],
    queryFn: async () => unwrap(await api.shopee.status.get()),
  });

  const connect = useMutation({
    mutationFn: async (provider: "mercadolivre" | "shopee") =>
      unwrap(
        provider === "shopee"
          ? await api.shopee.oauth.start.get({
              query: { returnTo: "connections" },
            })
          : await api.ml.oauth.start.get({
              query: { returnTo: "connections" },
            }),
      ),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title:
          error instanceof Error
            ? error.message
            : "Não foi possível iniciar a conexão",
      });
    },
  });

  const atAccountLimit = billing.data ? !billing.data.canConnectAccount : false;

  return (
    <>
      <PageHeader
        title="Conexões"
        description={
          billing.data ? (
            <>
              {billingUsageLabel(billing.data)}
              {atAccountLimit && (
                <>
                  {" · "}
                  <Link
                    to="/billing"
                    className="text-ember hover:underline"
                  >
                    Ver plano
                  </Link>
                </>
              )}
            </>
          ) : (
            "Lojas conectadas a esta organização"
          )
        }
        actions={
          shopee.data?.configured ? (
            <Menu>
              <MenuTrigger
                render={
                  <Button disabled={atAccountLimit} loading={connect.isPending}>
                    <Plus />
                    Conectar conta
                  </Button>
                }
              />
              <MenuContent align="end">
                <MenuItem onClick={() => connect.mutate("mercadolivre")}>
                  Mercado Livre
                </MenuItem>
                <MenuItem onClick={() => connect.mutate("shopee")}>
                  Shopee
                </MenuItem>
              </MenuContent>
            </Menu>
          ) : (
            <Tooltip
              content={
                atAccountLimit
                  ? "Limite de lojas do seu plano atingido"
                  : "Conectar outra loja"
              }
            >
              <span className="inline-flex">
                <Button
                  loading={connect.isPending}
                  disabled={atAccountLimit}
                  onClick={() => connect.mutate("mercadolivre")}
                >
                  <Plus />
                  Conectar conta
                </Button>
              </span>
            </Tooltip>
          )
        }
      />

      {accounts.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner className="size-5 text-ink-muted" />
        </div>
      ) : accounts.data && accounts.data.accounts.length > 0 ? (
        <>
          {imports.data && !imports.data.workerOnline ? (
            <p className="mb-3 text-2xs text-danger">
              Nenhum worker está consumindo a fila. As importações ficam
              esperando.{" "}
              <Link to="/activity" className="font-medium underline">
                Ver Atividade
              </Link>
            </p>
          ) : (
            <p className="mb-3 text-2xs text-ink-muted">
              Importe anúncios e pedidos primeiro. Acompanhe o progresso em{" "}
              <Link to="/activity" className="font-medium text-ember hover:underline">
                Atividade
              </Link>
              .
            </p>
          )}
          {["mercadolivre", "shopee"].map((provider) => {
            const group = accounts.data.accounts.filter(
              (account) => account.provider === provider,
            );
            if (group.length === 0) return null;
            return (
              <section key={provider} className="mb-6">
                <h2 className="mb-2 flex items-center gap-2 text-[0.8125rem] font-semibold text-ink">
                  <ChannelMark provider={provider} size="md" />
                  {channelLabel(provider)}
                  <span className="font-normal text-ink-muted">{group.length}</span>
                </h2>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {group.map((account) => (
                    <AccountCard
                      key={account.id}
                      account={account}
                      importJobs={imports.data?.jobs ?? []}
                      lastRuns={imports.data?.last ?? []}
                      ready={
                        imports.data?.ready.find(
                          (row) => row.accountId === account.id,
                        ) ?? {
                          accountId: account.id,
                          listingCount: 0,
                          orderCount: 0,
                        }
                      }
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </>
      ) : (
        <Card>
          <EmptyState
            icon={<Link2 />}
            title="Nenhuma conta conectada"
            description="Use a configuração inicial para conectar, importar e mapear SKUs em sequência."
            action={
              <Link
                to="/setup"
                className="text-2xs font-medium text-ember hover:underline"
              >
                Começar configuração →
              </Link>
            }
          />
        </Card>
      )}

      <ErpInboundCard />
      <ErpOutboundCard />
    </>
  );
}

function ErpInboundCard() {
  const inbound = useQuery({
    queryKey: ["integrations"],
    queryFn: async () => unwrap(await api.integrations.get()),
  });
  const source = inbound.data?.sources[0];

  return (
    <Link
      to="/connections/inbound"
      className="mt-4 block rounded-surface outline-none focus-visible:ring-2 focus-visible:ring-ember/25"
    >
      <Card className="transition-[border-color,transform] duration-100 ease-swift hover:border-ink-faint active:scale-[0.99]">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Cable className="size-3.5 text-ink-muted" />
            <CardTitle>ERP</CardTitle>
          </div>
          {source ? (
            <Badge variant={source.enabled ? "success" : "neutral"} dot>
              {source.enabled ? "Ativa" : "Rascunho"}
            </Badge>
          ) : null}
        </CardHeader>
        <CardContent className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-2xs text-ink-muted">
            {source
              ? "De-para visual, dry-run com diff e token para o seu sistema ou automação."
              : "Se o estoque mora no ERP, ele entra aqui e o Bridge empurra para o ML."}
          </p>
          <span className={buttonVariants({ variant: "secondary", size: "sm" })}>
            {source ? "Abrir de-para" : "Configurar entrada"}
          </span>
        </CardContent>
      </Card>
    </Link>
  );
}

function ErpOutboundCard() {
  const outbound = useQuery({
    queryKey: ["outbound"],
    queryFn: async () => unwrap(await api.integrations.outbound.get()),
  });
  const endpoints = outbound.data?.endpoints ?? [];
  const enabled = endpoints.filter((endpoint) => endpoint.enabled).length;

  return (
    <Link
      to="/connections/outbound"
      className="mt-4 block rounded-surface outline-none focus-visible:ring-2 focus-visible:ring-ember/25"
    >
      <Card className="transition-[border-color,transform] duration-100 ease-swift hover:border-ink-faint active:scale-[0.99]">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Radio className="size-3.5 text-ink-muted" />
            <CardTitle>Saída para o ERP</CardTitle>
          </div>
          {endpoints.length > 0 ? (
            <Badge variant={enabled > 0 ? "success" : "neutral"} dot>
              {enabled > 0
                ? `${enabled} ativo${enabled === 1 ? "" : "s"}`
                : "Pausada"}
            </Badge>
          ) : null}
        </CardHeader>
        <CardContent className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-2xs text-ink-muted">
            {endpoints.length > 0
              ? "Pedido pago, estoque e reclamação saem assinados. API de leitura no mesmo lugar."
              : "Webhooks assinados e API de leitura para o seu sistema puxar pedidos, estoque e reclamações."}
          </p>
          <span className={buttonVariants({ variant: "secondary", size: "sm" })}>
            {endpoints.length > 0 ? "Abrir webhooks" : "Configurar saída"}
          </span>
        </CardContent>
      </Card>
    </Link>
  );
}

type Account = {
  id: string;
  provider: string;
  displayName: string;
  externalAccountId: string;
  metadata: Record<string, unknown> | null;
  status: string;
  tokenExpiresAt: string | Date;
  createdAt: string | Date;
  capabilities?: {
    orders?: boolean;
    questions?: boolean;
    messages?: boolean;
    claims?: boolean;
    billing?: boolean;
    ads?: boolean;
    visits?: boolean;
  };
};

type ImportJob = {
  accountId: string;
  type: string;
  state: "waiting" | "active";
};

type SyncPath =
  | "sync-orders"
  | "sync-listings"
  | "sync-questions"
  | "sync-messages"
  | "sync-claims"
  | "sync-billing"
  | "sync-ads"
  | "sync-visits";

function useImportMutation(
  accountId: string,
  path: SyncPath,
  startedTitle: string,
) {
  const toast = useToast();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      unwrap(await api.ml.accounts({ id: accountId })[path].post()),
    onSuccess: (data) => {
      toast.add({
        type: data.alreadyRunning ? "info" : "success",
        title: data.alreadyRunning
          ? "Importação já em andamento"
          : startedTitle,
        description: data.alreadyRunning
          ? "Espere terminar antes de pedir de novo."
          : "Acompanhe o progresso em Atividade.",
      });
      void queryClient.invalidateQueries({ queryKey: ["sync-logs"] });
      void queryClient.invalidateQueries({ queryKey: ["imports"] });
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível enfileirar a importação",
        description:
          error instanceof Error ? error.message : "Tente de novo em instantes.",
      });
    },
  });
}

type ImportLastRun = {
  accountId: string;
  type: string;
  at: string;
  level: string;
  message: string;
};

function lastHint(last?: ImportLastRun) {
  if (!last) return "Ainda não importado";
  return `Última: ${formatRelativeTime(last.at)}`;
}

function AccountCard({
  account,
  importJobs,
  lastRuns,
  ready,
}: {
  account: Account;
  importJobs: ImportJob[];
  lastRuns: ImportLastRun[];
  ready: { accountId: string; listingCount: number; orderCount: number };
}) {
  const status = ACCOUNT_STATUS[account.status] ?? ACCOUNT_STATUS.connected!;
  const running = (type: SyncPath) =>
    importJobs.some((job) => job.accountId === account.id && job.type === type);

  const syncOrders = useImportMutation(
    account.id,
    "sync-orders",
    "Importação de pedidos iniciada",
  );
  const syncListings = useImportMutation(
    account.id,
    "sync-listings",
    "Importação de anúncios iniciada",
  );
  const syncQuestions = useImportMutation(
    account.id,
    "sync-questions",
    "Importação de perguntas iniciada",
  );
  const syncMessages = useImportMutation(
    account.id,
    "sync-messages",
    "Importação de mensagens iniciada",
  );
  const syncBilling = useImportMutation(
    account.id,
    "sync-billing",
    "Importação do extrato iniciada",
  );
  const syncClaims = useImportMutation(
    account.id,
    "sync-claims",
    "Importação de reclamações iniciada",
  );
  const syncAds = useImportMutation(
    account.id,
    "sync-ads",
    "Importação de Product Ads iniciada",
  );
  const syncVisits = useImportMutation(
    account.id,
    "sync-visits",
    "Importação de visitas iniciada",
  );
  const toast = useToast();
  const queryClient = useQueryClient();
  const syncAll = useMutation({
    mutationFn: async () =>
      unwrap(await api.ml.accounts({ id: account.id })["sync-all"].post()),
    onSuccess: (data) => {
      toast.add({
        type: "success",
        title:
          data.started > 0
            ? "Importação completa enfileirada"
            : "Importação já em andamento",
        description:
          data.started > 0
            ? `${data.started} jobs na fila, na ordem certa. Acompanhe em Atividade.`
            : "Espere terminar antes de pedir de novo.",
      });
      void queryClient.invalidateQueries({ queryKey: ["imports"] });
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível enfileirar a importação",
        description:
          error instanceof Error ? error.message : "Tente de novo em instantes.",
      });
    },
  });

  const lastOf = (type: SyncPath) =>
    lastRuns.find((row) => row.accountId === account.id && row.type === type);
  const hasListings = ready.listingCount > 0;
  const hasOrders = ready.orderCount > 0;
  const anyBusy = importJobs.some((job) => job.accountId === account.id);

  const ordersBusy = syncOrders.isPending || running("sync-orders");
  const listingsBusy = syncListings.isPending || running("sync-listings");
  const questionsBusy = syncQuestions.isPending || running("sync-questions");
  const messagesBusy = syncMessages.isPending || running("sync-messages");
  const claimsBusy = syncClaims.isPending || running("sync-claims");
  const billingBusy = syncBilling.isPending || running("sync-billing");
  const adsBusy = syncAds.isPending || running("sync-ads");
  const visitsBusy = syncVisits.isPending || running("sync-visits");

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <ChannelMark provider={account.provider} size="md" className="size-8 text-2xs" />
            <div>
              <p className="text-[0.8125rem] font-semibold text-ink">
                {account.displayName}
              </p>
              <p className="font-mono text-2xs text-ink-muted">
                {officialLabel(account)} · {account.externalAccountId}
              </p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Badge variant="neutral">{channelLabel(account.provider)}</Badge>
            <Badge variant={status.variant} dot>
              {status.label}
            </Badge>
          </div>
        </div>

        {account.status === "connected" || account.status === "token_expired" ? (
          <p className="text-2xs text-ink-muted">
            Token renova automaticamente · expira{" "}
            {formatDateTime(account.tokenExpiresAt)}
          </p>
        ) : (
          <p className="text-2xs text-ink-muted">
            Pedidos e anúncios importados continuam visíveis. Reconecte para
            voltar a sincronizar.
          </p>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <RenameAccountDialog account={account} />
          {account.status === "connected" ? (
            <>
              {account.capabilities?.orders && (
                <Tooltip content="Enfileira o que este canal oferece, na ordem certa">
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={syncAll.isPending || anyBusy}
                    onClick={() => syncAll.mutate()}
                  >
                    <Layers />
                    {syncAll.isPending || anyBusy
                      ? "Importando…"
                      : "Importar tudo"}
                  </Button>
                </Tooltip>
              )}
              {account.capabilities?.orders && (
                <Tooltip
                  content={`Importa os pedidos recentes desta conta. ${lastHint(lastOf("sync-orders"))}`}
                >
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={ordersBusy}
                    onClick={() => syncOrders.mutate()}
                  >
                    <ShoppingBag />
                    {ordersBusy ? "Importando…" : "Importar pedidos"}
                  </Button>
                </Tooltip>
              )}
              <Tooltip
                content={`Importa todos os anúncios desta conta. ${lastHint(lastOf("sync-listings"))}`}
              >
                <Button
                  variant="secondary"
                  size="sm"
                  loading={listingsBusy}
                  onClick={() => syncListings.mutate()}
                >
                  <Tags />
                  {listingsBusy ? "Importando…" : "Importar anúncios"}
                </Button>
              </Tooltip>
              {account.capabilities?.questions && (
                <Tooltip
                  content={`Importa perguntas sem resposta e as respondidas recentemente. ${lastHint(lastOf("sync-questions"))}`}
                >
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={questionsBusy}
                    onClick={() => syncQuestions.mutate()}
                  >
                    <MessageCircleQuestion />
                    {questionsBusy ? "Importando…" : "Importar perguntas"}
                  </Button>
                </Tooltip>
              )}
              {account.capabilities?.messages && (
                <Tooltip
                  content={
                    hasOrders
                      ? `Importa mensagens desta conta. ${lastHint(lastOf("sync-messages"))}`
                      : "Importe os pedidos primeiro — as mensagens se ligam a eles."
                  }
                >
                  <span className="inline-flex">
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={messagesBusy}
                      disabled={!hasOrders}
                      onClick={() => syncMessages.mutate()}
                    >
                      <MessageSquare />
                      {messagesBusy ? "Importando…" : "Importar mensagens"}
                    </Button>
                  </span>
                </Tooltip>
              )}
              {account.capabilities?.claims && (
                <Tooltip
                  content={`Importa reclamações e devoluções abertas e recentes. ${lastHint(lastOf("sync-claims"))}`}
                >
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={claimsBusy}
                    onClick={() => syncClaims.mutate()}
                  >
                    <Scale />
                    {claimsBusy ? "Importando…" : "Importar reclamações"}
                  </Button>
                </Tooltip>
              )}
              {account.capabilities?.billing && (
                <Tooltip
                  content={`Importa o que o ${channelLabel(account.provider)} cobrou de verdade (últimos 3 meses). ${lastHint(lastOf("sync-billing"))}`}
                >
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={billingBusy}
                    onClick={() => syncBilling.mutate()}
                  >
                    <Receipt />
                    {billingBusy ? "Importando…" : "Importar extrato"}
                  </Button>
                </Tooltip>
              )}
              {account.capabilities?.ads && (
                <Tooltip
                  content={`Importa o gasto de Product Ads dos últimos 3 meses. Sem conta de Ads, a importação termina vazia. ${lastHint(lastOf("sync-ads"))}`}
                >
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={adsBusy}
                    onClick={() => syncAds.mutate()}
                  >
                    <Megaphone />
                    {adsBusy ? "Importando…" : "Importar ads"}
                  </Button>
                </Tooltip>
              )}
              {account.capabilities?.visits && (
                <Tooltip
                  content={
                    hasListings
                      ? `Importa visitas (30 dias), saúde, campanhas e posição no card do catálogo. ${lastHint(lastOf("sync-visits"))}`
                      : "Importe os anúncios primeiro — as visitas consultam o catálogo local."
                  }
                >
                  <span className="inline-flex">
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={visitsBusy}
                      disabled={!hasListings}
                      onClick={() => syncVisits.mutate()}
                    >
                      <Eye />
                      {visitsBusy ? "Importando…" : "Importar visitas"}
                    </Button>
                  </span>
                </Tooltip>
              )}
              <DisconnectAccountDialog account={account} />
            </>
          ) : (
            <ReconnectButton account={account} />
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function billingUsageLabel(billing: {
  planLabel: string;
  limits: { accounts: number | null; ordersPerMonth: number | null };
  usage: { accounts: number; ordersThisMonth: number };
}) {
  const stores =
    billing.limits.accounts == null
      ? `${billing.usage.accounts} lojas`
      : `${billing.usage.accounts} de ${billing.limits.accounts} lojas`;
  const orders =
    billing.limits.ordersPerMonth == null
      ? `${billing.usage.ordersThisMonth} pedidos neste mês`
      : `${billing.usage.ordersThisMonth} de ${billing.limits.ordersPerMonth} pedidos neste mês`;
  return `${billing.planLabel} · ${stores} · ${orders}`;
}

function officialLabel(account: Account): string {
  const siteId =
    typeof account.metadata?.siteId === "string"
      ? account.metadata.siteId
      : null;
  const nickname =
    typeof account.metadata?.nickname === "string"
      ? account.metadata.nickname
      : null;
  if (siteId && nickname && nickname !== account.displayName) {
    return `${siteId} · ${nickname}`;
  }
  return siteId ?? account.provider;
}

function RenameAccountDialog({ account }: { account: Account }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(account.displayName);
  const toast = useToast();
  const queryClient = useQueryClient();

  const save = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.ml.accounts({ id: account.id }).patch({
          displayName: name.trim(),
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["ml-accounts"] });
      toast.add({ type: "success", title: "Apelido atualizado" });
      setOpen(false);
    },
    onError: () => {
      toast.add({
        type: "error",
        title: "Não foi possível salvar o apelido",
      });
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setName(account.displayName);
      }}
    >
      <DialogTrigger
        render={
          <Button variant="secondary" size="sm">
            <Pencil />
            Apelido
          </Button>
        }
      />
      <DialogContent
        title="Apelido da loja"
        description={`Nome curto para identificar esta conta nas demais telas. O nome oficial no ${channelLabel(account.provider)} não muda.`}
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (name.trim()) save.mutate();
          }}
        >
          <Field>
            <FieldLabel>Apelido</FieldLabel>
            <Input
              value={name}
              maxLength={80}
              required
              onChange={(event) => setName(event.target.value)}
            />
            <FieldDescription>
              {typeof account.metadata?.nickname === "string"
                ? `Conta oficial: ${account.metadata.nickname}`
                : `ID ${account.externalAccountId}`}
            </FieldDescription>
          </Field>
          <Button type="submit" loading={save.isPending} disabled={!name.trim()}>
            Salvar
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DisconnectAccountDialog({ account }: { account: Account }) {
  const [open, setOpen] = useState(false);
  const toast = useToast();
  const queryClient = useQueryClient();

  const disconnect = useMutation({
    mutationFn: async () =>
      unwrap(await api.ml.accounts({ id: account.id }).disconnect.post()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["ml-accounts"] });
      void queryClient.invalidateQueries({ queryKey: ["billing"] });
      void queryClient.invalidateQueries({ queryKey: ["setup"] });
      toast.add({
        type: "success",
        title: "Loja desvinculada",
        description: "O sync parou. Pedidos e anúncios importados ficam.",
      });
      setOpen(false);
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível desvincular",
        description:
          error instanceof Error ? error.message : "Tente de novo em instantes.",
      });
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        render={
          <Button variant="ghost" size="sm">
            <Unlink />
            Desvincular
          </Button>
        }
      />
      <DialogContent
        title="Desvincular esta loja?"
        description={`A autorização no ${channelLabel(account.provider)} é revogada. O histórico fica.`}
      >
        <div className="flex flex-col gap-4">
          <ul className="space-y-1.5 text-2xs text-ink-secondary">
            <li>
              A loja <span className="font-medium text-ink">{account.displayName}</span>{" "}
              deixa de sincronizar pedidos, anúncios e estoque.
            </li>
            <li>O slot do plano é liberado para conectar outra conta.</li>
            <li>
              Pedidos, anúncios e SKUs já importados continuam visíveis. Dá
              para reconectar depois sem perder o histórico.
            </li>
          </ul>
          <div className="flex justify-end gap-2">
            <DialogClose render={<Button variant="secondary">Voltar</Button>} />
            <Button
              variant="danger"
              loading={disconnect.isPending}
              onClick={() => disconnect.mutate()}
            >
              Desvincular
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReconnectButton({ account }: { account: Account }) {
  const toast = useToast();
  const reconnect = useMutation({
    mutationFn: async () =>
      unwrap(await api.ml.accounts({ id: account.id }).reconnect.post()),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível reconectar",
        description:
          error instanceof Error ? error.message : "Tente de novo em instantes.",
      });
    },
  });

  return (
    <Button
      variant="secondary"
      size="sm"
      loading={reconnect.isPending}
      onClick={() => reconnect.mutate()}
    >
      <Link2 />
      Reconectar
    </Button>
  );
}
